type ParamMap = Record<string, string>;
type InputParamMap = Record<string, string | number | boolean>;

/**
 * @description 从指定字符串中获取指定的参数内容
 * @param paraName 要读取的参数，设置为true则返回参数描述对象
 * @return 如果paraName是字符串则返回对应的参数值，如果paraName为true则返回所有url中的参数描述对象
 */
export function getParaFromString(str: string, paraName: string): string;
export function getParaFromString(str: string, paraName: true): ParamMap;
export function getParaFromString(
  str: string,
  paraName: string | true
): string | ParamMap {
  const data: ParamMap = {};
  const rawArray = str.match(/([^=&#?]+)=[^&#]+/g) || [];
  rawArray.forEach(function (para: string) {
    const d = para.split("=");
    const key = d[0];
    const val = decodeURIComponent(d[1]);
    if (data[key] !== undefined) {
      data[key] += "," + val;
    } else {
      data[key] = val;
    }
  });
  return paraName !== true ? data[paraName] || "" : data;
}

/**
 * 从指定字符串中删除指定的参数，可以指定多个参数，或者 true（删除所有参数）
 */
export function removeParamsFromString(
  url: string,
  paraName: string | string[] | true
): string {
  if (!paraName) {
    return url;
  }
  const arr1 = url.split("#");
  const arr2 = arr1[0].split("?");
  const base = arr2[0];
  const para = arr2.length > 1 ? arr2[1] : "";
  const hash = arr1.length > 1 ? "#" + arr1[1] : "";
  const paramList =
    typeof paraName === "string"
      ? [paraName]
      : Array.isArray(paraName)
      ? paraName
      : [];
  if (!paramList.length || !para) {
    return arr1[0] + hash;
  }
  // 转义可能的非安全字符，以创建可预期的正则表达式
  paramList.map((p) => p.replace(/([\\(){}[\]^$+\-*?|])/g, "\\$1"));
  return (
    (
      base +
      "?" +
      para
        .replace(
          new RegExp("(?:^|&)(?:" + paramList.join("|") + ")=[^&$]+", "g"),
          ""
        )
        .replace(/^&/, "")
    ).replace(/\?$/, "") + hash
  );
}

/**
 * 给指定的url字符串添加新的参数
 * @param url 要处理的url字符串
 * @param params 要添加的参数，比如 id=1&name=test 或者 {id: 1, name: "test"}
 * @param removeSamePara 是否移除同名的参数
 */
export function addParamsToString(
  url: string,
  params: string | InputParamMap,
  removeSamePara: boolean = false
) {
  const paramString =
    typeof params === "string"
      ? params
      : Object.keys(params)
          .map((key) => `${key}=${encodeURIComponent(params[key])}`)
          .join("&");
  if (!paramString) {
    return url;
  }
  // 跳过 hash 数据
  const splitedURL = url.split("#");
  // 先移除同名参数
  if (removeSamePara) {
    splitedURL[0] = removeParamsFromString(
      splitedURL[0],
      (paramString.match(/([^=&#?]+)=[^&#]+/g) || []).map((str) =>
        str.replace(/=.+$/, "")
      )
    );
  }
  const sp = splitedURL[0].indexOf("?") + 1 ? "&" : "?";
  return (
    splitedURL[0] +
    sp +
    paramString +
    (splitedURL.length > 1 ? "#" + splitedURL[1] : "")
  ).replace(/\?&/, "?");
}

/**
 * 从页面URL中获取指定的参数内容，如果 paraName 为 true 则返回所有参数
 */
export function getPara(paraName: string): string;
export function getPara(paraName: true): ParamMap;
export function getPara(paraName: string | true) {
  if (typeof paraName === "string") {
    return getParaFromString(location.search, paraName);
  }
  return getParaFromString(location.search, true);
}

/**
 * 从页面URL中获取指定的参数内容，如果 paraName 为 true 则返回所有参数
 */
export const getQueryString = getPara;

/**
 * 从页面 URL hash 中获取指定的参数内容，如果 paraName 为 true 则返回所有参数
 */
export function getHash(paraName: string): string;
export function getHash(paraName: true): ParamMap;
export function getHash(paraName: string | true) {
  if (typeof paraName === "string") {
    return getParaFromString(location.hash, paraName);
  }
  return getParaFromString(location.hash, true);
}

/**
 * @name removePara
 * @param url 要处理的url，如果不提供，则取当前页面的url
 * @param paraName 要移除的参数或数组，比如 "id" 或 ["name","id"] 或 true
 * @return 处理好的url字符串(不会修改当前页面的url地址)，如果paraName传递true，则删除所有的参数
 */
export function removePara(paraName: string | string[] | true): string {
  return removeParamsFromString(document.URL, paraName);
}

/**
 * 给 当前页面URL 添加指定参数，返回一个新的 URL 地址
 */
export function addPara(
  params: string | InputParamMap,
  removeSamePara: boolean = false
) {
  return addParamsToString(document.URL, params, removeSamePara);
}
